import {DateTime} from "luxon";
import type {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdMyDataSchema} from "ebird-mydata-reader";
import {getFamilySlug} from "../../../helpers/helpers";
import {toRootRelativeImagePath} from "../../../helpers/imagePaths";
import type {LocationEcoregionData, LocationRegionData} from "../../../helpers/types";
import type {SpeciesFamilyLink} from "../../../App";
import {
    normalizeHabitatMixLabel,
    BIRDING_HABITAT_RULES,
    getPreferredHabitatMix,
    getPreferredHabitatPrimary,
    getPreferredHabitatSecondary,
    toTitleCase
} from "../../../helpers/habitat-classification";
import {DEBUG_PERF, endPerfTimer, startPerfTimer} from "../../../helpers/perf";
import type {
    HotspotHabitatRuleOptionGroup,
    HotspotHabitatMetric,
    HotspotLocationSummaryItem,
    HotspotMediaItem,
    HotspotSpeciesHighlight,
    HotspotVisitSummary
} from "./types";
import {capitalizeFirstLetter} from "../../../helpers/strings";
import {getHotspotHabitatColor, HOTSPOT_HABITAT_RULE_GROUP_ORDER} from "./constants";

function getObservationTimestamp(observation: EBirdMyDataSchema) {
    if (!observation.date) return 0;

    const normalizedTime = observation.time?.trim();
    if (normalizedTime) {
        const parsedDateTime = DateTime.fromISO(`${observation.date}T${normalizedTime}`);
        if (parsedDateTime.isValid) return parsedDateTime.toMillis();
    }

    const parsedDate = DateTime.fromISO(observation.date);
    return parsedDate.isValid ? parsedDate.toMillis() : 0;
}

function getObservationVisitKey(observation: EBirdMyDataSchema) {
    return observation.submissionID ?? (observation as any).submissionId ?? `${observation.date}-${observation.time}-${observation.location}`;
}

function formatDate(date?: string) {
    if (!date) return '--';

    const parsedDate = DateTime.fromISO(date);
    return parsedDate.isValid ? parsedDate.toFormat('LLLL d, y') : date;
}

export function formatObservationDay(date?: string) {
    return formatDate(date);
}

function formatCoordinate(value?: number, positiveLabel?: string, negativeLabel?: string) {
    if (!Number.isFinite(value)) return '--';

    const direction = (value ?? 0) >= 0 ? positiveLabel : negativeLabel;
    return `${Math.abs(value as number).toFixed(4)}${direction ? ` ${direction}` : ''}`;
}

function slugifySummaryValue(value: string) {
    return value
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^\w]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function formatDistance(value?: number | null) {
    if (!Number.isFinite(value ?? NaN)) return 'Not available';
    const distanceKm = (value as number) / 1000;
    return distanceKm >= 10
        ? `${Math.round(distanceKm).toLocaleString()} km`
        : `${distanceKm.toFixed(1)} km`;
}

function formatElevation(value?: number | null, band?: string | null) {
    if (!Number.isFinite(value ?? NaN)) {
        return band || 'Not available';
    }

    const elevationMeters = Math.round(value as number);
    return `${elevationMeters.toLocaleString()} m`;
}

function formatBooleanFlag(value?: boolean | null) {
    if (typeof value !== 'boolean') return 'Not available';
    return value ? 'Yes' : 'No';
}

function formatMarineContext(value?: string | null) {
    if (!value) return 'Not available';

    switch (value) {
        case 'land_or_inland':
            return 'Land or inland';
        case 'nearshore_marine':
            return 'Nearshore marine';
        case 'pelagic_water':
            return 'Pelagic water';
        case 'unknown':
            return 'Unknown';
        default:
            return capitalizeFirstLetter(value.replace(/_/g, ' '));
    }
}

function getEcoDataString(value: unknown) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
}

function getEcoDataNumber(value: unknown) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}

export function getHabitatRuleOptionGroups(): HotspotHabitatRuleOptionGroup[] {
    const groupedRules = new Map<string, Set<string>>();

    BIRDING_HABITAT_RULES.forEach(rule => {
        const existingLabels = groupedRules.get(rule.family) ?? new Set<string>();
        existingLabels.add(rule.label);
        groupedRules.set(rule.family, existingLabels);
    });

    return HOTSPOT_HABITAT_RULE_GROUP_ORDER.reduce<HotspotHabitatRuleOptionGroup[]>((groups, groupLabel) => {
            const options = Array.from(groupedRules.get(groupLabel) ?? [])
                .sort((left, right) => left.localeCompare(right))
                .map(label => ({
                    label,
                    value: label
                }));

            if (!options.length) {
                return groups;
            }

            groups.push({
                label: groupLabel,
                options
            });

            return groups;
        }, []);
}

const hotspotCoordinatesCache = new WeakMap<EBirdMyDataSchema[], ReturnType<typeof getLocationCoordinates>>();
const hotspotVisitHistoryCache = new WeakMap<EBirdMyDataSchema[], HotspotVisitSummary[]>();
const hotspotMediaCache = new WeakMap<EBirdMyDataSchema[], HotspotMediaItem[]>();
const hotspotMediaSummaryCache = new WeakMap<HotspotMediaItem[], {itemCount: number, speciesCount: number}>();
const hotspotSpeciesHighlightsCache = new WeakMap<
    EBirdMyDataSchema[],
    {
        checklist: EBirdChecklistByFamily[];
        speciesImages: any;
        familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>;
        parentTaxonomicOrderByTaxonomicOrder: Map<number, number>;
        value: HotspotSpeciesHighlight[];
    }
>();

export function getLocationCoordinates(observations: EBirdMyDataSchema[]) {
    const coordinates = observations.filter(
        observation => Number.isFinite(observation.latitude) && Number.isFinite(observation.longitude)
    );

    if (!coordinates.length) return undefined;

    const totals = coordinates.reduce((accumulator, observation) => {
        accumulator.latitude += observation.latitude;
        accumulator.longitude += observation.longitude;
        return accumulator;
    }, {latitude: 0, longitude: 0});

    return {
        latitude: totals.latitude / coordinates.length,
        longitude: totals.longitude / coordinates.length
    };
}

export function getCachedLocationCoordinates(observations: EBirdMyDataSchema[]) {
    const cached = hotspotCoordinatesCache.get(observations);
    if (cached !== undefined) {
        if (DEBUG_PERF) console.debug('hotspotDetail:coordinates:cacheHit', {observationCount: observations.length});
        return cached;
    }

    const timer = startPerfTimer('hotspotDetail:coordinates:compute');
    const computed = getLocationCoordinates(observations);
    hotspotCoordinatesCache.set(observations, computed);
    endPerfTimer(timer);
    return computed;
}

function getSpeciesLookup(checklist: EBirdChecklistByFamily[]) {
    const speciesLookup = new Map<number, EBirdChecklistBySpecies>();

    for (const family of checklist) {
        for (const species of family.speciesList) {
            speciesLookup.set(species.taxonomicOrder, species);

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                speciesLookup.set(alternateTaxonomicOrder, species);
            }
        }
    }

    return speciesLookup;
}

export function getHotspotVisitHistory(observations: EBirdMyDataSchema[]) {
    const visitMap = new Map<string, HotspotVisitSummary>();

    for (const observation of observations) {
        const visitKey = getObservationVisitKey(observation);
        const existing = visitMap.get(visitKey);
        const timestamp = getObservationTimestamp(observation);
        const speciesKey = observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';

        if (!existing) {
            visitMap.set(visitKey, {
                visitKey,
                date: observation.date,
                time: observation.time,
                location: observation.location,
                observationCount: 1,
                speciesCount: speciesKey ? 1 : 0,
                timestamp
            });
            continue;
        }

        existing.observationCount += 1;
        existing.speciesCount += speciesKey ? 1 : 0;
        if (timestamp > existing.timestamp) {
            existing.date = observation.date;
            existing.time = observation.time;
            existing.location = observation.location;
            existing.timestamp = timestamp;
        }
    }

    return Array.from(visitMap.values()).sort((a, b) => b.timestamp - a.timestamp);
}

export function getCachedHotspotVisitHistory(observations: EBirdMyDataSchema[]) {
    const cached = hotspotVisitHistoryCache.get(observations);
    if (cached) {
        if (DEBUG_PERF) console.debug('hotspotDetail:visitHistory:cacheHit', {observationCount: observations.length});
        return cached;
    }

    const timer = startPerfTimer('hotspotDetail:visitHistory:compute');
    const computed = getHotspotVisitHistory(observations);
    hotspotVisitHistoryCache.set(observations, computed);
    endPerfTimer(timer);
    return computed;
}

export function getLocationSummary(
    locationId: string,
    observations: EBirdMyDataSchema[],
    ecoData?: LocationEcoregionData,
    regionData?: LocationRegionData
): HotspotLocationSummaryItem[] {
    const coordinates = getLocationCoordinates(observations);
    const coordinateHref = coordinates
        ? `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`
        : undefined;
    const ecoregionHref = ecoData?.ecoregionName
        ? `https://www.oneearth.org/ecoregions/${slugifySummaryValue(ecoData.ecoregionName)}/`
        : undefined;
    const realmHref = ecoData?.realm
        ? `https://en.wikipedia.org/wiki/${encodeURIComponent(ecoData.realm.replace(/\s+/g, '_'))}_realm`
        : undefined;
    const ebirdHotspotHref = locationId ? `https://ebird.org/hotspot/${encodeURIComponent(locationId)}` : undefined;

    const regionLabel = regionData?.country || regionData?.subnational1 || regionData?.subnational2
        ? [
            regionData.labels?.county || regionData.subnational2?.shortName,
            regionData.labels?.state || regionData.subnational1?.shortName,
            regionData.labels?.country || regionData.country?.shortName
        ].filter(Boolean).join(', ')
        : '';

    return [
        regionLabel ? {
            label: 'Region',
            value: regionLabel
        } : null,
        {
            label: 'eBird hotspot',
            value: locationId || 'Not available',
            href: ebirdHotspotHref
        },
        {
            label: 'Coordinates',
            value: coordinates ? `${formatCoordinate(coordinates.latitude, 'N', 'S')}, ${formatCoordinate(coordinates.longitude, 'E', 'W')}` : '--',
            href: coordinateHref
        },
        {
            label: 'Elevation',
            value: formatElevation(ecoData?.terrain_elevation_m, ecoData?.terrain_elevation_band)
        },
        {
            label: 'Ecoregion',
            value: ecoData?.ecoregionName || 'Not available',
            href: ecoregionHref
        },
        {
            label: 'Biome',
            value: ecoData?.biomeName || 'Not available'
        },
        {
            label: 'Realm',
            value: ecoData?.realm || 'Not available',
            href: realmHref
        },
        {
            label: 'Primary habitat',
            value: toTitleCase(getPreferredHabitatPrimary(ecoData)) || 'Not available'
        },
        {
            label: 'Inland water proximity',
            value: ecoData?.water_proximity_label || formatDistance(ecoData?.water_distance_m)
        },
        {
            label: 'Coast proximity',
            value: formatDistance(ecoData?.distance_to_coast_m)
        },
        {
            label: 'Coastal context',
            value: ecoData?.coastal_context ? capitalizeFirstLetter(ecoData.coastal_context.replace(/_/g, ' ')) : 'Not available'
        },
        {
            label: 'Ocean point',
            value: formatBooleanFlag(ecoData?.marine_context?.isOcean)
        },
        {
            label: 'Marine context',
            value: formatMarineContext(ecoData?.marine_context?.marineContext)
        }
    ].filter((item): item is HotspotLocationSummaryItem => Boolean(item));
}

export function getHabitatMetrics(ecoData?: LocationEcoregionData): HotspotHabitatMetric[] {
    const habitatMix = ecoData ? getPreferredHabitatMix(ecoData) : undefined;
    if (!habitatMix || typeof habitatMix !== 'object') return [];

    return Object.entries(habitatMix)
        .map(([key, value]) => ({
            key,
            label: toTitleCase(normalizeHabitatMixLabel(key)),
            percent: Math.round((getEcoDataNumber(value) ?? 0) * 100),
            color: getHotspotHabitatColor(key)
        }))
        .filter(metric => metric.percent > 0)
        .sort((a, b) => b.percent - a.percent);
}

export function getHotspotSpeciesHighlights(
    observations: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    speciesImages: any,
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>
): HotspotSpeciesHighlight[] {
    const speciesLookup = getSpeciesLookup(checklist);
    const speciesMap = new Map<number, HotspotSpeciesHighlight & {
        visitIds: Set<string>,
        firstTimestamp: number,
        lastTimestamp: number,
        liferCount: number,
        firstOfYearCount: number
    }>();

    for (const observation of observations) {
        const rawTaxonomicOrder = observation.taxonomicOrder;
        if (!Number.isFinite(rawTaxonomicOrder)) continue;

        const parentTaxonomicOrder = parentTaxonomicOrderByTaxonomicOrder.get(rawTaxonomicOrder) ?? rawTaxonomicOrder;
        const species = speciesLookup.get(parentTaxonomicOrder) ?? speciesLookup.get(rawTaxonomicOrder);
        const familyLink = familyByTaxonomicOrder.get(parentTaxonomicOrder) ?? familyByTaxonomicOrder.get(rawTaxonomicOrder);
        const timestamp = getObservationTimestamp(observation);
        const visitId = getObservationVisitKey(observation);
        const existing = speciesMap.get(parentTaxonomicOrder);

        if (!existing) {
            const commonName = species?.commonName || observation.commonName || 'Unknown species';
            speciesMap.set(parentTaxonomicOrder, {
                taxonomicOrder: parentTaxonomicOrder,
                commonName,
                scientificName: species?.scientificName || observation.scientificName,
                imageUrl: toRootRelativeImagePath(
                    species?.image?.thumb || speciesImages?.[parentTaxonomicOrder]?.thumb || speciesImages?.[rawTaxonomicOrder]?.thumb
                ) || '/assets/fallback-bird.svg',
                visitCount: 1,
                observationCount: 1,
                firstSeen: observation.date,
                lastSeen: observation.date,
                detailPath: species?.speciesCode && familyLink ? `/family-explorer/family/${getFamilySlug(familyLink.familyName)}/species/${species.speciesCode}` : undefined,
                visitIds: new Set([visitId]),
                firstTimestamp: timestamp,
                lastTimestamp: timestamp,
                liferCount: observation.isLifer ? 1 : 0,
                firstOfYearCount: observation.isFirstOfYear ? 1 : 0
            });
            continue;
        }

        existing.observationCount += 1;
        if (observation.isLifer) existing.liferCount += 1;
        if (observation.isFirstOfYear) existing.firstOfYearCount += 1;

        if (!existing.visitIds.has(visitId)) {
            existing.visitIds.add(visitId);
            existing.visitCount += 1;
        }

        if (timestamp && (existing.firstTimestamp === 0 || timestamp < existing.firstTimestamp)) {
            existing.firstTimestamp = timestamp;
            existing.firstSeen = observation.date;
        }

        if (timestamp >= existing.lastTimestamp) {
            existing.lastTimestamp = timestamp;
            existing.lastSeen = observation.date;
        }
    }

    return Array.from(speciesMap.values())
        .map(({visitIds, firstTimestamp, lastTimestamp, liferCount, firstOfYearCount, ...species}) => ({
            ...species,
            _priority: {
                liferCount,
                firstOfYearCount
            }
        }))
        .sort((a, b) => {
            if (b._priority.liferCount !== a._priority.liferCount) return b._priority.liferCount - a._priority.liferCount;
            if (b._priority.firstOfYearCount !== a._priority.firstOfYearCount) return b._priority.firstOfYearCount - a._priority.firstOfYearCount;
            if (b.visitCount !== a.visitCount) return b.visitCount - a.visitCount;
            if (b.observationCount !== a.observationCount) return b.observationCount - a.observationCount;
            return a.commonName.localeCompare(b.commonName);
        })
        .map(({_priority, ...species}) => species);
}

export function getCachedHotspotSpeciesHighlights(
    observations: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    speciesImages: any,
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>
) {
    const cached = hotspotSpeciesHighlightsCache.get(observations);
    if (
        cached
        && cached.checklist === checklist
        && cached.speciesImages === speciesImages
        && cached.familyByTaxonomicOrder === familyByTaxonomicOrder
        && cached.parentTaxonomicOrderByTaxonomicOrder === parentTaxonomicOrderByTaxonomicOrder
    ) {
        if (DEBUG_PERF) console.debug('hotspotDetail:speciesHighlights:cacheHit', {observationCount: observations.length});
        return cached.value;
    }

    const timer = startPerfTimer('hotspotDetail:speciesHighlights:compute');
    const computed = getHotspotSpeciesHighlights(
        observations,
        checklist,
        speciesImages,
        familyByTaxonomicOrder,
        parentTaxonomicOrderByTaxonomicOrder
    );

    hotspotSpeciesHighlightsCache.set(observations, {
        checklist,
        speciesImages,
        familyByTaxonomicOrder,
        parentTaxonomicOrderByTaxonomicOrder,
        value: computed
    });
    endPerfTimer(timer);

    return computed;
}

export function getHotspotMedia(observations: EBirdMyDataSchema[]) {
    const mediaItems = observations.reduce((items, observation) => {
        const numbers = getEcoDataString(observation.mlCatalogNumbers)
            .split(/[\s,;|]+/)
            .map(catalogNumber => catalogNumber.trim().replace(/^ML/i, ''))
            .filter(Boolean);

        for (const catalogNumber of numbers) {
            items.push({
                catalogNumber,
                observation
            });
        }

        return items;
    }, [] as HotspotMediaItem[]);

    const seenCatalogNumbers = new Set<string>();

    return mediaItems
        .sort((a, b) => getObservationTimestamp(b.observation) - getObservationTimestamp(a.observation))
        .filter(item => {
            if (seenCatalogNumbers.has(item.catalogNumber)) return false;
            seenCatalogNumbers.add(item.catalogNumber);
            return true;
        });
}

export function getCachedHotspotMedia(observations: EBirdMyDataSchema[]) {
    const cached = hotspotMediaCache.get(observations);
    if (cached) {
        if (DEBUG_PERF) console.debug('hotspotDetail:media:cacheHit', {observationCount: observations.length});
        return cached;
    }

    const timer = startPerfTimer('hotspotDetail:media:compute');
    const computed = getHotspotMedia(observations);
    hotspotMediaCache.set(observations, computed);
    endPerfTimer(timer);
    return computed;
}

export function getHotspotMediaSummary(mediaItems: HotspotMediaItem[]) {
    const speciesSeen = new Set(mediaItems.map(item => item.observation.taxonomicOrder).filter(Number.isFinite));

    return {
        itemCount: mediaItems.length,
        speciesCount: speciesSeen.size
    };
}

export function getCachedHotspotMediaSummary(mediaItems: HotspotMediaItem[]) {
    const cached = hotspotMediaSummaryCache.get(mediaItems);
    if (cached) {
        if (DEBUG_PERF) console.debug('hotspotDetail:mediaSummary:cacheHit', {mediaCount: mediaItems.length});
        return cached;
    }

    const timer = startPerfTimer('hotspotDetail:mediaSummary:compute');
    const computed = getHotspotMediaSummary(mediaItems);
    hotspotMediaSummaryCache.set(mediaItems, computed);
    endPerfTimer(timer);
    return computed;
}

export function getSpeciesSeenDateRange(speciesHighlights: HotspotSpeciesHighlight[]) {
    if (!speciesHighlights.length) return 'No species recorded here yet.';

    const firstSeen = speciesHighlights
        .map(species => species.firstSeen)
        .filter(Boolean)
        .sort()[0];
    const lastSeen = speciesHighlights
        .map(species => species.lastSeen)
        .filter(Boolean)
        .sort()
        .slice(-1)[0];

    if (!firstSeen && !lastSeen) return 'No species recorded here yet.';
    if (firstSeen && lastSeen && firstSeen !== lastSeen) return `${formatDate(firstSeen)} to ${formatDate(lastSeen)}`;
    return formatDate(firstSeen || lastSeen);
}

export function formatObservationDate(date?: string, time?: string) {
    if (!date) return '--';

    const parsedDate = DateTime.fromISO(date);
    const timeLabel = time?.trim() || 'All day';
    return `${parsedDate.isValid ? parsedDate.toFormat('LLLL d, y') : date} ${timeLabel}`;
}
